import React from "react";
import { useNavigate } from "react-router-dom";
import {
    BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, Label,
} from "recharts";
import { ArrowRight, Check, TrendingUp, AlertTriangle, Sparkles, CheckCircle2, Lock, GitCommitHorizontal } from "lucide-react";
import FunderShell from "./FunderShell";
import { SectionCard, MetricCard, ScorePill, toneColor, composite } from "./FunderUI";
import { getOverview, getProposals } from "../../service/FunderPortalService";
import { useTheme } from "../ThemeContext";

export default function FunderCallOverview() {
    return (
        <FunderShell activePage="Call Overview">
            {(callId, call) => <Overview callId={callId} call={call} />}
        </FunderShell>
    );
}

const ATTN_ICON = { alert: AlertTriangle, sparkle: Sparkles, check: CheckCircle2 };

function buildHistogram(proposals) {
    const bins = [
        { label: "40", min: 40, max: 50, count: 0 },
        { label: "50", min: 50, max: 60, count: 0 },
        { label: "60", min: 60, max: 70, count: 0 },
        { label: "70", min: 70, max: 80, count: 0 },
        { label: "80", min: 80, max: 90, count: 0 },
        { label: "90", min: 90, max: 101, count: 0 },
    ];
    proposals.forEach((p) => {
        const c = composite(p);
        const b = bins.find((x) => c >= x.min && c < x.max);
        if (b) b.count += 1;
    });
    return bins;
}

function Overview({ callId, call }) {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const overview = getOverview(callId);
    const proposals = getProposals(callId);

    if (!overview) {
        return <div className="funder-empty">No overview available for this call.</div>;
    }

    const m = overview.metrics;
    const pctChange = Math.round(((call.totalSubmissions - call.prevRoundTotal) / call.prevRoundTotal) * 100);
    const histogram = buildHistogram(proposals);
    const toneText = (tone) => ({ green: "var(--success)", amber: "var(--series-amber)", red: "var(--danger-muted)", blue: "var(--info)" }[tone] || "var(--ink-500)");

    const labelVal = (label, value) => (
        <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.06em", color: "#8fb3a6", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 12.5, color: "#eef3f0", lineHeight: 1.45 }}>{value}</div>
        </div>
    );

    return (
        <div className="funder-section-stack">
            {/* Header band */}
            <div className="funder-page-header">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-start" }}>
                    <div style={{ flex: "1 1 380px", minWidth: 0 }}>
                        <div className="funder-overline">{call.funder} · {call.scheme}</div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: "6px 0", fontSize: 11.5, color: "#8fb3a6" }}>
                            <GitCommitHorizontal size={14} />
                            {`Stages 1–${call.activeStep + 1} · Eligibility, scoring & shortlisting`}
                        </div>
                        <h1 className="funder-h1">{call.title}</h1>
                        <p className="funder-subtitle">
                            {`Your workspace for ${call.totalSubmissions} submissions. DeepThought surfaces signals; you coordinate expert review and the committee process.`}
                        </p>
                        <button className="funder-btn" style={{ marginTop: 14, background: "#5ecfa0", color: "#10271f" }} onClick={() => navigate("/funder/submissions")}>
                            Open Submissions Review <ArrowRight size={15} />
                        </button>
                    </div>

                    <div style={{ background: "rgba(255,255,255,0.06)", borderLeft: "3px solid #5ecfa0", borderRadius: 12, padding: "16px 18px", flex: "0 1 340px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                            <strong style={{ color: "#fff", fontSize: 14 }}>Call Context</strong>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "#8fb3a6" }}><Lock size={12} /> Pre-configured</span>
                        </div>
                        {labelVal("Scheme", call.scheme)}
                        {labelVal("Round", call.round)}
                        {labelVal("Priorities", call.priorities.join(" · "))}
                        {labelVal("Criteria", call.criteria.map((c) => `${c.label} — ${c.text}`).join(" · "))}
                        {labelVal("Funding range", call.fundingRange)}
                        {labelVal("Expert review", call.expertReview)}
                    </div>
                </div>
            </div>

            {/* Stage stepper */}
            <SectionCard>
                <div className="funder-stepper">
                    {call.stages.map((s, idx) => {
                        const complete = idx < call.activeStep;
                        const active = idx === call.activeStep;
                        return (
                            <div className="funder-step" key={s}>
                                <div className={`funder-step-dot${complete ? " funder-step-dot-complete" : active ? " funder-step-dot-active" : ""}`}>
                                    {complete ? <Check size={15} /> : idx + 1}
                                </div>
                                <div className={`funder-step-label${active ? " funder-step-label-active" : ""}`}>{s}</div>
                            </div>
                        );
                    })}
                </div>
                <p style={{ fontSize: 12, color: "var(--ink-350)", marginTop: 14, marginBottom: 0, textAlign: "center" }}>
                    DeepThought analysis runs continuously across stages 2–4, providing intelligence to support each step.
                </p>
            </SectionCard>

            {/* Metric cards */}
            <div className="funder-metric-grid">
                <MetricCard
                    label="Total Submissions"
                    value={call.totalSubmissions}
                    sub={<span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><TrendingUp size={12} color="var(--success)" /> +{pctChange}% vs previous round</span>}
                />
                <MetricCard label="Avg DeepThought Score" value={m.avgScore}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
                        <ScorePill dimKey="novelty" value={m.avgByDim.novelty} size="sm" />
                        <ScorePill dimKey="methodology" value={m.avgByDim.methodology} size="sm" />
                        <ScorePill dimKey="impact" value={m.avgByDim.impact} size="sm" />
                    </div>
                </MetricCard>
                <MetricCard label="Strategic Alignment" value={`${m.alignmentPct}%`} sub={m.alignmentText} />
                <MetricCard label="Review Progress" value={`${m.reviewProgressPct}%`} sub={m.reviewProgressText}>
                    <div className="funder-bar-track" style={{ marginTop: 8 }}>
                        <div className="funder-bar-fill" style={{ width: `${m.reviewProgressPct}%`, background: "var(--success)" }} />
                    </div>
                </MetricCard>
            </div>

            {/* Score distribution */}
            <SectionCard kicker="Score Distribution" title="Proposal distribution by DeepThought score">
                <div style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                        <AreaChart data={histogram} margin={{ top: 10, right: 16, bottom: 24, left: 4 }}>
                            <defs>
                                <linearGradient id="scoreDensity" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--series-blue)" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="var(--series-blue)" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--ink-400)" }} tickLine={false} axisLine={{ stroke: "var(--border)" }}>
                                <Label value="Composite DeepThought score" position="bottom" offset={8} style={{ fontSize: 11, fill: "var(--ink-350)" }} />
                            </XAxis>
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--ink-400)" }} tickLine={false} axisLine={false} width={28} />
                            <Tooltip cursor={{ stroke: "var(--border)" }} contentStyle={{ fontSize: 12, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }} labelStyle={{ color: "var(--ink-900)" }} formatter={(v) => [`${v} proposals`, "Count"]} />
                            <Area type="monotone" dataKey="count" stroke="var(--series-blue)" strokeWidth={2.5} fill="url(#scoreDensity)" dot={{ r: 3, fill: "var(--series-blue)", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
                <p style={{ fontSize: 12, color: "var(--ink-350)", marginTop: 8, marginBottom: 0 }}>
                    DeepThought scores are directional signals — all proposals receive expert review.
                </p>
            </SectionCard>

            {/* Submission alignment */}
            <SectionCard kicker="Submissions × Call Priorities" title="Submission alignment"
                subtitle="How submissions map to the stated call priorities, plus emerging themes DeepThought identified across the pool.">
                <div style={{ overflowX: "auto" }}><table className="funder-table">
                    <thead>
                        <tr>
                            <th>Priority / Theme</th>
                            <th>Submissions</th>
                            <th>Avg DT Score</th>
                            <th>Top Proposal</th>
                            <th>Assessment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {overview.priorities.map((p) => (
                            <tr key={p.name}>
                                <td style={{ fontWeight: 600, color: "var(--ink-900)" }}>{p.name}</td>
                                <td>{p.count}</td>
                                <td><span className="funder-dt-chip">{p.avgScore}</span></td>
                                <td style={{ color: "var(--ink-500)" }}>{p.top}</td>
                                <td style={{ color: toneText(p.tone), fontWeight: 600 }}>{p.assessment}</td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={5} style={{ background: "var(--surface-2)", color: "var(--ink-350)", fontSize: 11.5, fontStyle: "italic" }}>
                                Emerging themes — identified by DeepThought outside stated priorities
                            </td>
                        </tr>
                        {overview.emergingThemes.map((p) => (
                            <tr key={p.name}>
                                <td style={{ fontWeight: 600, color: "var(--ink-900)" }}>
                                    <span className="funder-tag" style={{ marginRight: 8, background: "var(--info-bg)", color: "var(--info)" }}>New</span>
                                    {p.name}
                                </td>
                                <td>{p.count}</td>
                                <td><span className="funder-dt-chip">{p.avgScore}</span></td>
                                <td style={{ color: "var(--ink-500)" }}>{p.top}</td>
                                <td style={{ color: toneText(p.tone), fontWeight: 600 }}>{p.assessment}</td>
                            </tr>
                        ))}
                    </tbody>
                </table></div>
                <p style={{ fontSize: 12, color: "var(--ink-350)", marginTop: 12, marginBottom: 0 }}>
                    {overview.unclustered} submissions did not cluster into any priority or emerging theme.
                </p>
            </SectionCard>

            {/* Pipeline + attention */}
            <div className="funder-grid-2">
                <SectionCard kicker="Status breakdown" title="Review pipeline">
                    <div style={{ width: "100%", height: 240 }}>
                        <ResponsiveContainer>
                            <BarChart data={overview.stageBreakdown} margin={{ top: 10, right: 16, bottom: 4, left: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                                <XAxis dataKey="name" interval={0} tick={{ fontSize: 10, fill: "var(--ink-400)" }} tickLine={false} axisLine={{ stroke: "var(--border)" }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--ink-400)" }} tickLine={false} axisLine={false} width={28} />
                                <Tooltip cursor={{ fill: "var(--surface-2)" }} contentStyle={{ fontSize: 12, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }} labelStyle={{ color: "var(--ink-900)" }} itemStyle={{ color: theme === "dark" ? "var(--ink-900)" : "#000" }} />
                                <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={64}>
                                    {overview.stageBreakdown.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </SectionCard>

                <SectionCard kicker="Program lead actions" title="Attention required">
                    {overview.attention.map((a, i) => {
                        const Icon = ATTN_ICON[a.icon] || AlertTriangle;
                        const c = toneColor(a.tone);
                        return (
                            <div className="funder-list-row" key={i}>
                                <div className="funder-list-icon" style={{ background: `${c}1f` }}>
                                    <Icon size={15} color={c} />
                                </div>
                                <div>
                                    <div className="funder-list-title">{a.title}</div>
                                    <div className="funder-list-detail">{a.detail}</div>
                                </div>
                            </div>
                        );
                    })}
                </SectionCard>
            </div>

            {/* Recent activity */}
            <SectionCard kicker="Reviewer & system events" title="Recent activity">
                {overview.activity.map((a, i) => (
                    <div className="funder-list-row" key={i} style={{ alignItems: "center" }}>
                        <div style={{ flex: 1 }}>
                            <span style={{ fontSize: 13, color: "var(--ink-900)" }}>
                                <strong>{a.who}</strong> {a.what} <span className="funder-grant-no">{a.target}</span>{a.after ? ` ${a.after}` : ""}
                            </span>
                            <div className="funder-list-detail">{a.when}</div>
                        </div>
                        <span className="funder-link" onClick={() => navigate(`/funder/proposal?id=${a.id}`)}>
                            View <ArrowRight size={13} />
                        </span>
                    </div>
                ))}
            </SectionCard>
        </div>
    );
}
